import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  UseDataGridConfig,
  useDataGridMasterDetailConfig,
} from '~/components/DataGridCustom';

import { ContractDetailsDataGridField, ContractDetailsDataGridRow } from './ContractDetails.types';

export const useContractDetailsDataGridConfig: UseDataGridConfig<ContractDetailsDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDetailsWidget.table' });

  const { defaultToggleColumnDefinition, ...masterDetailProps } =
    useDataGridMasterDetailConfig<ContractDetailsDataGridRow>();

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        // required to include toggle column definition to preserve
        // column order on remote state reset invocation
        defaultToggleColumnDefinition,
        createColumn({
          field: ContractDetailsDataGridField.POSITION,
          minWidth: 60,
          hideable: false,
          sortable: false,
        }),
        createColumn({
          field: ContractDetailsDataGridField.TITLE,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 50,
          flex: 1,
          sortable: false,
        }),
        createColumn({
          field: ContractDetailsDataGridField.UNIT,
          renderCell: cellFactory(CuriosityValue, ({ value }) => ({ ns: CuriosityNamespaceType.UNIT, i18nKey: value })),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: ContractDetailsDataGridField.PRICE_PER_UNIT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: ContractDetailsDataGridField.PLANNED_QUANTITY,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: ContractDetailsDataGridField.PLANNED_VOLUME,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
      ],
      ...masterDetailProps,
    } as DataGridConfig<ContractDetailsDataGridRow>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tColumn, t, defaultToggleColumnDefinition]);
};
